import shallowCopyOf from 'lodash/clone';
import {
  type AddRegisterDataSeriesToGraphProps,
  type DragSelectedRegisterProps,
  type RemoveGraphProps,
  type RemoveRegisterProps,
  type SetSelectedRegisterDataSeriesGraphProps,
} from '@common/store/actions/types';
import { compositeKey, getPaletteIndex } from '@common/store/reducers/graphs/colorReducers';
import {
  type GraphsPayloadAction,
  type GraphsStoreState,
} from '@common/store/reducers/graphs/types';
import { type InstallationId } from '@common/types/brandedTypes';
import {
  configurationForDataPoint,
  normalizeSelectedRegisterDataSeriesGraphs,
  type RegisterDataSeries,
  removeDataPoint,
  type SelectedRegisterDataSeriesGraph,
} from '@common/types/models/SelectedDataPoints';

export const restoreReducer = (
  compositeState: GraphsStoreState,
  action: GraphsPayloadAction<Record<InstallationId, SelectedRegisterDataSeriesGraph[]>>,
) => {
  const { targetTab, data: restoredDataPoints } = action.payload;
  const state = compositeState[targetTab];

  try {
    state.selectedRegisterDataSeriesGraphs = Object
      .keys(restoredDataPoints)
      .reduce((accumulator, key) => {
        const installationId = Number(key) as InstallationId;
        return {
          ...accumulator,
          [installationId]: restoredDataPoints[installationId]
            .map((graph): SelectedRegisterDataSeriesGraph => ({
              registerDataSeriesArray: graph.registerDataSeriesArray.map((item) => ({
                ...item,
                ...configurationForDataPoint(state.graphConfiguration[installationId]),
                colorIndex: getPaletteIndex(
                  item.registerIndex,
                  item.valueBitMask,
                  compositeState.shared,
                ),
              })),
            })),
        };
      }, {});
  } catch {
    state.selectedRegisterDataSeriesGraphs = {};
  }
};

export const setSelectedReducer = (
  compositeState: GraphsStoreState,
  action: GraphsPayloadAction<SetSelectedRegisterDataSeriesGraphProps>,
) => {
  const {
    targetTab,
    data: {
      installationId,
      selectedRegisterDataSeriesGraphs,
      noColor,
    },
  } = action.payload;
  const state = compositeState[targetTab];

  const registerDataSeriesArray = noColor
    ? selectedRegisterDataSeriesGraphs
    : selectedRegisterDataSeriesGraphs.map(
      (graph): SelectedRegisterDataSeriesGraph => ({
        ...graph,
        registerDataSeriesArray: graph.registerDataSeriesArray.map((
          item,
        ): RegisterDataSeries => ({
          ...item,
          ...configurationForDataPoint(state.graphConfiguration[installationId]),
          colorIndex: getPaletteIndex(
            item.registerIndex,
            item.valueBitMask,
            compositeState.shared,
          ),
        })),
      }),
    );
  state.selectedRegisterDataSeriesGraphs[installationId] =
    normalizeSelectedRegisterDataSeriesGraphs(
      registerDataSeriesArray,
    );
};

export const addRegisterReducer = (
  compositeState: GraphsStoreState,
  action: GraphsPayloadAction<AddRegisterDataSeriesToGraphProps>,
) => {
  const {
    targetTab,
    data: {
      installationId,
      selectedDataPoint,
      valueBitMask,
      toNewGraph,
      noColor,
    },
  } = action.payload;
  const state = compositeState[targetTab];

  const updatedItems = shallowCopyOf(
    state.selectedRegisterDataSeriesGraphs[installationId] ?? [],
  );
  if (toNewGraph || updatedItems.length === 0) {
    updatedItems.push({ registerDataSeriesArray: [] });
  }
  const existingRegisterIndexes = updatedItems[updatedItems.length - 1]
    .registerDataSeriesArray.map(compositeKey);
  if (existingRegisterIndexes.includes(compositeKey(selectedDataPoint))) {
    return; // No duplicates
  }

  const toAdd: RegisterDataSeries = {
    ...selectedDataPoint,
    valueBitMask,
    ...configurationForDataPoint(state.graphConfiguration[installationId]),
  };
  if (!noColor) {
    toAdd.colorIndex = getPaletteIndex(
      selectedDataPoint.registerIndex,
      valueBitMask,
      compositeState.shared,
    );
  }
  updatedItems[updatedItems.length - 1].registerDataSeriesArray.push(toAdd);

  state.selectedRegisterDataSeriesGraphs[installationId] =
    normalizeSelectedRegisterDataSeriesGraphs(
      updatedItems,
    );
};

export const dragRegisterReducer = (
  compositeState: GraphsStoreState,
  action: GraphsPayloadAction<DragSelectedRegisterProps>,
) => {
  const {
    targetTab,
    data: {
      installationId,
      registerIndex,
      valueBitMask,
      fromGroup,
      toGroup,
      toNewGraph,
    },
  } = action.payload;
  const state = compositeState[targetTab];

  if (toGroup || toNewGraph) {
    const updatedItems = shallowCopyOf(state.selectedRegisterDataSeriesGraphs[installationId]);
    const draggedDataPoint = removeDataPoint(
      updatedItems,
      registerIndex,
      valueBitMask,
      fromGroup,
    );
    if (draggedDataPoint) {
      if (toNewGraph) {
        updatedItems.push({ registerDataSeriesArray: [] });
      }
      const existingRegisterIndexes = updatedItems[toGroup - 1]
        .registerDataSeriesArray.map(compositeKey);
      if (existingRegisterIndexes.includes(compositeKey(draggedDataPoint))) {
        return; // No duplicates
      }
      updatedItems[toGroup - 1].registerDataSeriesArray.push(draggedDataPoint);
    }

    state.selectedRegisterDataSeriesGraphs[installationId] =
      normalizeSelectedRegisterDataSeriesGraphs(
        updatedItems,
      );
  }
};

export const removeRegisterReducer = (
  compositeState: GraphsStoreState,
  action: GraphsPayloadAction<RemoveRegisterProps>,
) => {
  const { targetTab, data: { installationId, registerIndex, valueBitMask, graphId } } =
    action.payload;
  const state = compositeState[targetTab];

  const updatedItems = shallowCopyOf(state.selectedRegisterDataSeriesGraphs[installationId]);
  removeDataPoint(
    updatedItems,
    registerIndex,
    valueBitMask,
    graphId,
  );

  state.selectedRegisterDataSeriesGraphs[installationId] =
    normalizeSelectedRegisterDataSeriesGraphs(
      updatedItems,
    );
};

export const removeGraphReducer = (
  compositeState: GraphsStoreState,
  action: GraphsPayloadAction<RemoveGraphProps>,
) => {
  const { targetTab, data: { groupId, installationId } } = action.payload;
  const state = compositeState[targetTab];

  const index = groupId - 1;
  const updatedItems = shallowCopyOf(state.selectedRegisterDataSeriesGraphs[installationId]);
  updatedItems.splice(index, 1);

  state.selectedRegisterDataSeriesGraphs[installationId] =
    normalizeSelectedRegisterDataSeriesGraphs(
      updatedItems,
    );
};

export const moveRegistersReducer = (
  compositeState: GraphsStoreState,
  action: GraphsPayloadAction<RemoveGraphProps>,
) => {
  const { targetTab, data: { groupId, installationId } } = action.payload;
  const state = compositeState[targetTab];

  const index = groupId - 1;
  const updatedItems = shallowCopyOf(state.selectedRegisterDataSeriesGraphs[installationId]);
  const [deletedGraph] = updatedItems.splice(index, 1);
  // If last graph was removed, then move items to the new last graph.
  const targetIndex = Math.min(updatedItems.length - 1, index);
  const existingRegisterIndexes = updatedItems[targetIndex].registerDataSeriesArray.map(
    compositeKey,
  );
  deletedGraph.registerDataSeriesArray
    .filter((registerDataSeries) =>
      !existingRegisterIndexes.includes(compositeKey(registerDataSeries))
    ) // No duplicates
    .forEach((registerDataSeries) => {
      updatedItems[targetIndex].registerDataSeriesArray.push(registerDataSeries);
    });
  state.selectedRegisterDataSeriesGraphs[installationId] =
    normalizeSelectedRegisterDataSeriesGraphs(
      updatedItems,
    );
};
